import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../Components/Sidebar";
import BottomBar from "../Components/BottomBar";
import TopBar from "../Components/TopBar";
import { Toaster, toast } from "react-hot-toast";
import { registerVoter, updateVoter, getVotersByElection } from "../services/VoterService";
import { getAllElections } from "../services/ElectionService";
import { Menu, CircleCheck } from "lucide-react";

const VoterRegistration = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isEdit = searchParams.get("edit") === "true";
    const voterData = location.state;
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        id: voterData ? voterData.id : "",
        name: voterData ? voterData.name || "" : "",
        designation: voterData ? voterData.designation || "" : "",
        email: voterData ? voterData.email || "" : "",
        age: voterData ? voterData.age || "" : "",
        nationalId: voterData ? voterData.nationalId || "" : "",
        gender: voterData ? voterData.gender || "" : "",
        electionId: voterData ? voterData.electionId || "" : "",
    });

    const [selectedElection, setSelectedElection] = useState(voterData ? voterData.electionId : "");
    const [elections, setElections] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    const [qrCode, setQrCode] = useState(null);

    // Handle responsive sidebar
    useEffect(() => {
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 768);
        };
        window.addEventListener('resize', handleResize);

        const handleSidebarToggle = (event) => {
            if (event.detail && typeof event.detail.expanded === "boolean") {
                setIsSidebarOpen(event.detail.expanded);
            }
        };
        window.addEventListener("sidebarToggle", handleSidebarToggle);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener("sidebarToggle", handleSidebarToggle);
        };
    }, []);

    useEffect(() => {
        getAllElections()
            .then((response) => setElections(response))
            .catch(() => toast.error("Failed to load elections"));
    }, []);

    const toggleSidebar = () => {
        const newState = !isSidebarOpen;
        setIsSidebarOpen(newState);
        window.dispatchEvent(new CustomEvent("sidebarToggle", { detail: { expanded: newState } }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleElectionChange = (e) => {
        const electionId = e.target.value;
        setSelectedElection(electionId);
        setFormData((prev) => ({ ...prev, electionId }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.id.trim()) errors.id = "Voter ID is required.";
        if (!formData.name.trim()) errors.name = "Voter name is required.";
        if (!formData.designation.trim()) errors.designation = "Designation is required.";
        if (!formData.email.trim()) {
            errors.email = "Email is required.";
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) errors.email = "Invalid email address.";
        }
        if (!formData.age.toString().trim()) errors.age = "Age is required.";
        if (Number(formData.age) <= 0) errors.age = "Age must be a positive number.";
        if (!formData.nationalId.trim()) errors.nationalId = "National ID is required.";
        if (!formData.gender.trim()) errors.gender = "Gender is required.";
        if (!selectedElection) errors.electionId = "Please select an election.";
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            toast.error("Please fix the errors in the form.");
            return;
        }
        try {
            const election = elections.find((ev) => ev.id === selectedElection);
            if (!election) {
                toast.error("Invalid election selected.");
                return;
            }
            const currentVoters = await getVotersByElection(selectedElection);
            if (!isEdit && currentVoters.length >= election.voterCount) {
                toast.error("Voter limit for this election has been reached.");
                return;
            }
        } catch (error) {
            toast.error("Error checking voter limit.");
            return;
        }
        if (isEdit) {
            const updateData = new FormData();
            Object.keys(formData).forEach((key) => updateData.append(key, formData[key]));
            try {
                await updateVoter(voterData.id, updateData);
                toast.success("Voter updated successfully!");
            } catch (error) {
                toast.error("Failed to update voter.");
            }
        } else {
            const data = new FormData();
            Object.keys(formData).forEach((key) => data.append(key, formData[key]));
            try {
                await registerVoter(data);
                toast.success("Voter registered successfully!");
                navigate("/Voters");
            } catch (error) {
                toast.error("Failed to register voter.");
            }
        }
    };

    const handleClear = () => {
        setFormData({
            id: "",
            name: "",
            designation: "",
            email: "",
            age: "",
            nationalId: "",
            gender: "",
            electionId: "",
        });
        setSelectedElection("");
        setFormErrors({});
    };

    const contentClass = isSidebarOpen
        ? "sm:ml-64 w-full sm:w-[calc(100%-16rem)]"
        : "ml-16 w-[calc(100%-4rem)] md:w-[calc(100%-4rem)] mx-auto flex justify-center";

    const innerContainerClass = !isSidebarOpen ? "w-full max-w-4xl" : "w-full";

    return (
        <div className="flex flex-col h-screen bg-gray-200 relative">
            <TopBar>
                <button onClick={toggleSidebar} className="p-2 mr-2" aria-label="Toggle sidebar">
                    <Menu size={24} />
                </button>
            </TopBar>
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
            <Toaster />
            <div className={`flex-1 transition-all duration-300 mt-16 ${contentClass}`}>
                <div className={`p-3 md:p-10 h-[calc(100vh-4rem)] overflow-auto ${innerContainerClass}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-5">
                        <div className="mt-2 md:mt-5">
                            <h2 className="text-xl md:text-2xl font-bold">
                                {isEdit ? "Edit Voter" : "Register Voter"}
                            </h2>
                        </div>
                    </div>
                    <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 w-full">
                        <form className="space-y-4 md:space-y-6" onSubmit={handleSubmit}>
                            {/* Election Selection */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Select Election</label>
                                <select
                                    name="electionId"
                                    value={selectedElection}
                                    onChange={handleElectionChange}
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                >
                                    <option value="">-- Select an Election --</option>
                                    {elections.map((election) => (
                                        <option key={election.id} value={election.id}>
                                            {election.name} ({election.electionDate})
                                        </option>
                                    ))}
                                </select>
                                {formErrors.electionId && <p className="text-red-500 text-xs mt-1">{formErrors.electionId}</p>}
                            </div>
                            {/* Voter ID */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Voter ID</label>
                                <input
                                    name="id"
                                    type="text"
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                    placeholder="Enter voter ID"
                                    value={formData.id}
                                    onChange={handleChange}
                                    required
                                />
                                {formErrors.id && <p className="text-red-500 text-xs mt-1">{formErrors.id}</p>}
                            </div>
                            {/* Name */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Name of the Voter</label>
                                <input
                                    name="name"
                                    type="text"
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                    placeholder="Enter voter's name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />
                                {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                            </div>
                            {/* Designation */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Current Designation</label>
                                <input
                                    name="designation"
                                    type="text"
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                    placeholder="Enter designation"
                                    value={formData.designation}
                                    onChange={handleChange}
                                    required
                                />
                                {formErrors.designation && <p className="text-red-500 text-xs mt-1">{formErrors.designation}</p>}
                            </div>
                            {/* Email */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Voter's Email</label>
                                <input
                                    name="email"
                                    type="email"
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                    placeholder="Enter voter's email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                                {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                            </div>
                            {/* Age */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Age</label>
                                <input
                                    name="age"
                                    type="number"
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                    placeholder="Enter age"
                                    value={formData.age}
                                    onChange={handleChange}
                                    required
                                />
                                {formErrors.age && <p className="text-red-500 text-xs mt-1">{formErrors.age}</p>}
                            </div>
                            {/* NIC */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">National Identity Card Number</label>
                                <input
                                    name="nationalId"
                                    type="text"
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                    placeholder="Enter NIC"
                                    value={formData.nationalId}
                                    onChange={handleChange}
                                    required
                                />
                                {formErrors.nationalId && <p className="text-red-500 text-xs mt-1">{formErrors.nationalId}</p>}
                            </div>
                            {/* Gender */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Gender</label>
                                <select
                                    name="gender"
                                    className="bg-gray-200 text-gray-600 font-medium py-1 md:py-2 px-4 md:px-8 text-sm rounded-lg cursor-pointer shadow-sm hover:bg-gray-300 transition"
                                    value={formData.gender}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="" disabled>Select</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select>
                                {formErrors.gender && <p className="text-red-500 text-xs mt-1">{formErrors.gender}</p>}
                            </div>
                            {/* Buttons */}
                            <div className="flex flex-col sm:flex-row justify-center sm:justify-end w-full space-y-3 sm:space-y-0 sm:space-x-4 mt-6">
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    className="bg-gray-300 text-black text-sm md:text-base font-semibold rounded-full px-4 md:px-6 py-1.5 md:py-2 hover:bg-gray-400 transition w-full sm:w-auto"
                                >
                                    Clear
                                </button>
                                <button
                                    type="submit"
                                    className="bg-black text-white text-sm md:text-base font-semibold rounded-full px-4 md:px-6 py-1.5 md:py-2 hover:bg-gray-700 transition flex items-center justify-center w-full sm:w-auto"
                                >
                                    Confirm
                                    <CircleCheck className="ml-2" size={window.innerWidth < 768 ? 16 : 20} />
                                </button>
                            </div>
                        </form>
                    </div>
                    {qrCode && (
                        <div className="mt-4 bg-white rounded-2xl shadow-lg p-4 w-full">
                            <h2 className="text-lg font-semibold mb-2">Your QR Code</h2>
                            <img src={qrCode} alt="Voter QR Code" className="mx-auto max-w-xs" />
                        </div>
                    )}
                </div>
            </div>
            <BottomBar />
        </div>
    );
};

export default VoterRegistration;
